import {Component, computed, ElementRef, HostListener, OnInit, signal, TemplateRef, ViewChild, ViewContainerRef} from '@angular/core';
import {UserService} from '../services/user/user.service';
import {User} from '../classes/user.class';
import {RegistrationService} from '../services/registration.service';
import {Utils} from "../utils.class";
import {AzureProjectPR, AzurePullRequest, AzureService, AzureTask, QueryLinks} from '../services/azure.service';
import {CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef} from '@angular/cdk/overlay';
import {PortalModule, TemplatePortal} from '@angular/cdk/portal';
import {ProjectShortNamePipe} from '../pipes/project-short-name.pipe';
import {FilterPipe} from '../pipes/filter.pipe';
import {CodaltComponent} from '../codalt.component';
import {DatePipe, NgOptimizedImage, SlicePipe, formatDate} from '@angular/common';
import {ChangedByNameSplittingPipe} from '../pipes/changed-by-name-splitting.pipe';
import {CodaltDatePipe} from '../pipes/codalt-date.pipe';
import {SortedTaskBackgroundColorPipe} from '../pipes/sorted-task-background-color.pipe';
import {Platform} from '../classes/platform';
import {PlatformService} from '../services/platform.service';
import {UrlPrefixStrippingPipe} from '../pipes/url-prefix-stripping.pipe';
import {UrlCombiningPipe} from '../pipes/url-combining.pipe';
import {LeaveService} from '../services/leave.service';
import {forkJoin} from 'rxjs';
import {Title} from '@angular/platform-browser';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {LeaveType} from '../classes/leave';
import {AuthorisationService} from '../services/auth/authorisation.service';
import {AbsentGradientPipe} from '../pipes/absent-gradient.pipe';

interface CustomContext {
    azure_task: AzureTask,
    platform: null | Platform[],
    relatedPRs?: { project: string; pr: AzurePullRequest }[]
}

@Component({
    selector: 'app-daily',
    templateUrl: './daily.component.html',
    styleUrls: ['./daily.component.scss'],
    imports: [
        ProjectShortNamePipe,
        FilterPipe,
        DatePipe,
        SlicePipe,
        CdkDropList,
        CdkDrag,
        CdkDragHandle,
        NgOptimizedImage,
        ChangedByNameSplittingPipe,
        CodaltDatePipe,
        SortedTaskBackgroundColorPipe,
        PortalModule,
        UrlPrefixStrippingPipe,
        UrlCombiningPipe,
        MatIcon,
        MatIconButton,
        MatTooltip,
        AbsentGradientPipe,
    ]
})
export class DailyComponent extends CodaltComponent implements OnInit {
    @ViewChild('taskContextMenu') taskContextMenuTpl: TemplateRef<unknown>;
    @ViewChild('taskSearch') taskSearch: ElementRef;
    readonly contextMenuTask = signal<null | CustomContext>(null);
    readonly weekList = signal<string[]>([]);
    readonly openPRs = signal<AzureProjectPR[]>([]);

    readonly taskSearchTerm = signal<string>('');
    readonly users = signal<User[]>([]);
    readonly showColleagues = signal<boolean>(false);
    readonly showChanged = signal<boolean>(false);
    readonly lastSelectedTask = signal<number>(null);
    readonly userPRs = signal<{ [userId: number]: AzureProjectPR[] }>({});
    readonly userTasks = signal<{ [userId: number]: AzureTask[] }>({});
    readonly platforms = signal<Platform[]>(null)
    readonly userAbsentDates = signal<{ [userId: number]: Map<string, { color: string | null; startMinutes: number; endMinutes: number; leaveTypeName?: string; begintime?: string; endtime?: string }> }>({})
    readonly queryLinks = signal<QueryLinks[]>([]);
    readonly leaveTypes = signal<LeaveType[]>(null);

    readonly absentLookup = computed(() => {
        const absentDates = this.userAbsentDates();
        const lookup: { [userId: number]: { [week: string]: { [isoDay: number]: { absent: boolean; color: string | null; startMinutes: number; endMinutes: number; leaveTypeName?: string; begintime?: string; endtime?: string } } } } = {};
        for (const userId of Object.keys(absentDates).map(Number)) {
            lookup[userId] = {};
            for (const weekStr of this.weekList()) {
                const monday = this.weekStringToMonday(weekStr);
                lookup[userId][weekStr] = {};
                for (let isoDay = 1; isoDay <= 7; isoDay++) {
                    const date = new Date(monday);
                    date.setUTCDate(monday.getUTCDate() + (isoDay === 1 ? 6 : isoDay - 2));
                    const dateStr = date.toISOString().slice(0, 10);
                    const entryMap = absentDates[userId];
                    const entry = entryMap?.get(dateStr) ?? null;
                    const absent = entry !== null;
                    lookup[userId][weekStr][isoDay] = {
                        absent,
                        color: absent ? entry.color : null,
                        startMinutes: absent ? entry.startMinutes : 0,
                        endMinutes: absent ? entry.endMinutes : 0,
                        leaveTypeName: absent ? entry.leaveTypeName : undefined,
                        begintime: absent ? entry.begintime : undefined,
                        endtime: absent ? entry.endtime : undefined,
                    };
                }
            }
        }
        return lookup;
    });

    private contextMenuOverlayRef: OverlayRef | null = null;

    constructor(
        private azureService: AzureService,
        private leaveService: LeaveService,
        private overlay: Overlay,
        private platformService: PlatformService,
        private registrationService: RegistrationService,
        private titleService: Title,
        private userService: UserService,
        private viewContainerRef: ViewContainerRef,
    ) {
        super();
        this.titleService.setTitle(`Daily ${this.environment.titleAppend}`);
    }

    today: Date = new Date();
    weeklyHours: any = {};
    utils = Utils;

    @HostListener('document:click')
    closeContextMenu(): void {
        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;
    }

    ngOnInit(): void {
        this.showColleagues.set(AuthorisationService.hasRole(this.Roles.Management));
        this.showChanged.set(localStorage.getItem('daily_show_changed') === 'true');
        this.lastSelectedTask.set(localStorage.getItem('last_selected_task') ? +localStorage.getItem('last_selected_task') : null);

        this.subscriptions.add(
            this.userService.getList().subscribe(users => {
                this.users.set(
                    users.filter(user => {
                        const start = user['employment_start'] ? new Date(user['employment_start']) : null;
                        const end = user['employment_end'] ? new Date(user['employment_end']) : null;
                        const startOk = !start || start <= this.today;
                        const endOk = !end || end >= this.today;
                        return startOk && endOk;
                    })
                );
                this.generateWeekList();
                this.loadWeeklyHours();

                // Load PRs and Tasks in parallel, then attach PRs to tasks
                this.subscriptions.add(
                    forkJoin({
                        prs: this.azureService.getOpenPullRequests(),
                        tasks: this.azureService.getOpenTasks()
                    }).subscribe(({prs, tasks}) => {
                        this.openPRs.set(prs || []);

                        // Map PRs per user
                        this.mapPRsPerUser();

                        const userTasks: { [userId: number]: AzureTask[] } = {};
                        const emailToUserId: { [email: string]: number } = {};
                        this.users().forEach(user => {
                            if (user.email) {
                                emailToUserId[user.email.toLowerCase()] = user.id;
                            }
                        });
                        (tasks || []).forEach((task: AzureTask) => {
                            const email = task.assignedTo?.email?.toLowerCase();
                            const userId = emailToUserId[email];
                            if (userId) {
                                if (!userTasks[userId]) userTasks[userId] = [];
                                userTasks[userId].push(task);
                            }
                        });
                        Object.keys(userTasks).forEach(userId => {
                            userTasks[+userId] = this.sortTasksForUser(+userId, userTasks[+userId]);
                        });

                        // Both PRs and Tasks are loaded, now attach them
                        this.attachRelatedPRsToTasks(userTasks);
                        this.userTasks.set(userTasks);
                    })
                );

                this.loadQueryLinks();
            })
        );

        this.subscriptions.add(
            this.platformService.getList().subscribe({
                next: (platforms) => {
                    this.platforms.set(platforms.map(p => ({...p})));
                },
                error: err => console.error(err)
            })
        );
    }

    generateWeekList() {
        const today = new Date();
        const getISOWeekYear = (d: Date): [number, number] => {
            const dt = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
            dt.setUTCDate(dt.getUTCDate() + 4 - (dt.getUTCDay() || 7));
            const year = dt.getUTCFullYear();
            const week = Math.ceil((((dt.getTime() - new Date(Date.UTC(year, 0, 1)).getTime()) / 86400000) + 1) / 7);
            return [year, week];
        };
        const [curYear, curWeek] = getISOWeekYear(today);
        const isoDay = today.getUTCDay() || 7; // 1=Mon...7=Sun
        const otherWeekDate = new Date(today);
        otherWeekDate.setDate(otherWeekDate.getDate() + (isoDay >= 4 ? 7 : -7));
        const [otherYear, otherWeek] = getISOWeekYear(otherWeekDate);
        if (isoDay >= 4) {
            this.weekList.set([`${curYear}-${curWeek}`, `${otherYear}-${otherWeek}`]);
        } else {
            this.weekList.set([`${otherYear}-${otherWeek}`, `${curYear}-${curWeek}`]);
        }
    }

    loadWeeklyHours() {
        this.subscriptions.add(
            this.registrationService.getWeeklyHours().subscribe(res => {
                this.weeklyHours = res;
            })
        );
        const prevMonday = this.weekStringToMonday(this.weekList()[0]);
        const sunday = new Date(this.weekStringToMonday(this.weekList()[1]));
        sunday.setUTCDate(sunday.getUTCDate() + 6);

        this.subscriptions.add(
            forkJoin({
                leaves: this.leaveService.getLeaves(true, prevMonday, sunday),
                leaveTypes: this.leaveService.getLeaveTypes()
            }).subscribe(({leaves, leaveTypes}) => {
                this.leaveTypes.set(leaveTypes);
                const leaveTypeColorMap = new Map(leaveTypes.map(lt => [lt.key, lt.color ?? null]));
                const leaveTypeNameMap = new Map(leaveTypes.map(lt => [lt.key, lt.name]));

                const map: { [userId: number]: Map<string, { color: string | null; startMinutes: number; endMinutes: number; leaveTypeName?: string; begintime?: string; endtime?: string }> } = {};
                const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                for (const leave of leaves) {
                    if (!leave.begindate || !leave.enddate) continue;
                    const affectedUsers = leave.common ? this.users() : leave.users;
                    if (!affectedUsers?.length) continue;
                    const color = leaveTypeColorMap.get(leave.leave_type) ?? null;
                    const start = new Date(leave.begindate), end = new Date(leave.enddate);
                    const leaveStart = new Date(Date.UTC(start.getFullYear(), start.getMonth(), start.getDate()));
                    const leaveEnd = new Date(Date.UTC(end.getFullYear(), end.getMonth(), end.getDate()));
                    const intervalWeek = leave.interval_week ?? 0;

                    // Parse begintime/endtime (HH:mm:ss) to minutes from midnight
                    const beginParts = (leave.begintime || '00:00:00').split(':').map(Number);
                    const endParts = (leave.endtime || '23:59:00').split(':').map(Number);
                    const startMinutes = beginParts[0] * 60 + (beginParts[1] || 0);
                    const endMinutes = endParts[0] * 60 + (endParts[1] || 0);

                    // Loop from prevMonday to sunday, bounded by leave start/end
                    const loopFrom = new Date(Math.max(prevMonday.getTime(), leaveStart.getTime()));
                    const loopUntil = new Date(Math.min(sunday.getTime(), leaveEnd.getTime()));

                    for (const d = new Date(loopFrom); d <= loopUntil; d.setUTCDate(d.getUTCDate() + 1)) {
                        let generateThisDay = intervalWeek < 1;

                        if (intervalWeek > 0) {
                            const week = +formatDate(d, 'w', 'nl');
                            const firstWeek = +formatDate(leaveStart, 'w', 'nl');
                            const thisWeek = ((week - firstWeek) % intervalWeek) === 0;
                            const weekDayName = formatDate(d, 'EEEE', 'en').toLowerCase();
                            const thisDay = !!leave[`repeat_${weekDayName}`];
                            generateThisDay = thisWeek && thisDay;
                        }

                        if (generateThisDay) {
                            for (const user of affectedUsers) {
                                if (!map[user.id]) map[user.id] = new Map();
                                map[user.id].set(d.toISOString().slice(0, 10), {
                                    color,
                                    startMinutes,
                                    endMinutes,
                                    leaveTypeName: leaveTypeNameMap.get(leave.leave_type),
                                    begintime: leave.begintime,
                                    endtime: leave.endtime
                                });
                            }
                        }
                    }
                }
                this.userAbsentDates.set(map);
            })
        );
    }

    loadQueryLinks() {
        const iconMap: Record<string, string> = {
            'b650e79e-cd92-44de-8d23-f5423a7092c0': 'fa-list-check',
            '0d9d1c1e-f025-455d-8622-c335d4edccdf': 'fa-users-slash',
            'b1a2efe3-5ef9-4975-b301-9de6fbd34f46': 'fa-helmet-safety',
            '28e4f2c7-0b91-444f-ac16-47f13cd2fa19': 'fa-person-chalkboard',
            '1ed11d36-368b-4ee5-bd22-17da95d70b95': 'fa-bug',
            '917c1217-5533-4177-905e-68dcb0257996': 'fa-phone',
        };

        this.subscriptions.add(
            this.azureService.getQueryLinks().subscribe({
                next: res => this.queryLinks.set(
                    res.map(filter => ({...filter, icon: iconMap[filter.id] ?? 'fa-question'}))
                ),
                error: () => {
                }
            })
        );
    }

    private mapPRsPerUser(): void {
        const userPRs: { [userId: number]: AzureProjectPR[] } = {};
        const emailToUserId: { [email: string]: number } = {};
        this.users().forEach(user => {
            if (user.email) {
                emailToUserId[user.email.toLowerCase()] = user.id;
            }
        });
        this.openPRs().forEach(projectPR => {
            const userProjectMap: { [userId: number]: AzureProjectPR } = {};
            projectPR.prs.forEach(pr => {
                const email = pr.createdBy?.uniqueName?.toLowerCase();
                const userId = emailToUserId[email];
                if (userId) {
                    if (!userProjectMap[userId]) {
                        userProjectMap[userId] = {project: projectPR.project, prs: []};
                    }
                    userProjectMap[userId].prs.push(pr);
                }
            });
            Object.keys(userProjectMap).forEach(userId => {
                if (!userPRs[+userId]) userPRs[+userId] = [];
                userPRs[+userId].push(userProjectMap[userId]);
            });
        });
        this.userPRs.set(userPRs);
    }

    private attachRelatedPRsToTasks(userTasks: { [userId: number]: AzureTask[] }): void {
        Object.values(userTasks).forEach(tasks => {
            tasks.forEach(task => {
                const taskIds = [task.id, task.parent_id, task.grandparent_id].filter(id => !!id);
                const relatedPRs: { project: string; pr: AzurePullRequest }[] = [];
                this.openPRs().forEach(projectPR => {
                    projectPR.prs.forEach(pr => {
                        const hasRelatedWorkItem = pr.workItems?.some(wi => taskIds.includes(wi.id));
                        if (hasRelatedWorkItem) {
                            relatedPRs.push({project: projectPR.project, pr});
                        }
                    });
                });
                task.relatedPRs = relatedPRs;
            });
        });
    }


    openContextMenu(event: MouseEvent, task: AzureTask): void {
        event.preventDefault();
        event.stopPropagation();

        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;

        const companyPlatforms = this.platforms().filter(platform => platform?.company?.azure_project_id === task.project_id);
        const filterPlatforms = companyPlatforms.filter(platform => {
            const findResult = platform.azure_features?.find(af => {
                return af.azure_feature_id === task.grandparent_id || af.azure_feature_id === task.parent_id;
            });
            return findResult !== undefined;
        });

        this.contextMenuTask.set({
            azure_task: task,
            platform: filterPlatforms?.length ? filterPlatforms : companyPlatforms,
            relatedPRs: task.relatedPRs ?? []
        });

        this.contextMenuOverlayRef = this.overlay.create({
            positionStrategy: this.getContextMenuPositionStrategy(event),
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });

        this.contextMenuOverlayRef.attach(new TemplatePortal(this.taskContextMenuTpl, this.viewContainerRef));
    }

    private getContextMenuPositionStrategy(event: MouseEvent): FlexibleConnectedPositionStrategy {
        const fallbackPositions: ConnectedPosition[] = [
            {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top'},
            {originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom'},
            {originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top'},
            {originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'bottom'},
        ];

        return this.overlay.position()
            .flexibleConnectedTo({x: event.clientX, y: event.clientY})
            .withPositions(fallbackPositions)
            .withViewportMargin(8)
            .withPush(true);
    }

    openContextMenuForPR(event: MouseEvent, pr: AzurePullRequest, projectName: string): void {
        event.preventDefault();
        event.stopPropagation();

        this.contextMenuOverlayRef?.dispose();
        this.contextMenuOverlayRef = null;

        // Create a synthetic task object to display PR in context menu
        const syntheticTask: AzureTask = {
            id: pr.pullRequestId,
            title: pr.title,
            parent_title: null,
            parent_id: null,
            parent_workItemType: null,
            grandparent_title: null,
            grandparent_id: null,
            grandparent_workItemType: null,
            state: pr.status,
            project: projectName,
            project_id: null,
            workItemType: 'Pull Request',
            severity: null,
            assignedTo: null,
            changed_by: pr.createdBy?.displayName,
            changed_date: pr.creationDate,
            url: pr.url,
            relatedPRs: [{project: projectName, pr}]
        };

        this.contextMenuTask.set({
            azure_task: syntheticTask,
            platform: null,
            relatedPRs: [{project: projectName, pr}]
        });

        this.contextMenuOverlayRef = this.overlay.create({
            positionStrategy: this.getContextMenuPositionStrategy(event),
            scrollStrategy: this.overlay.scrollStrategies.close(),
        });

        this.contextMenuOverlayRef.attach(new TemplatePortal(this.taskContextMenuTpl, this.viewContainerRef));
    }

    onTaskSearch(value: string): void {
        // this uses regex, the regex is to see if the provided value is numeric, if any part of the value is not numeric it will be false
        if (/^\d+$/.test(value.trim())) {
            window.open(`https://dev.azure.com/codalt/_workitems/edit/${value.trim()}`, '_blank');
            return;
        }
        const firstTask = this.getFirstMatchingTask();
        if (firstTask) {
            window.open(`https://dev.azure.com/codalt/${firstTask.project}/_workitems/edit/${firstTask.id}`, '_blank');
        } else {
            window.open(`https://dev.azure.com/codalt/_search?text=${value}&type=workitem&pageSize=25`, '_blank');
        }
    }

    getFirstMatchingTask(): AzureTask | null {
        if (!this.taskSearchTerm().trim()) {
            return null;
        }
        for (const user of this.users()) {
            const match = this.userTasks()[user.id]?.find(task => this.taskMatchesSearch(task));
            if (match) return match;
        }
        return null;
    }

    taskMatchesSearch(task: AzureTask): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return true;
        }

        // Check task properties
        const taskMatches = task.title?.toLowerCase().includes(term) ||
            task.parent_title?.toLowerCase().includes(term) ||
            task.grandparent_title?.toLowerCase().includes(term) ||
            task.project?.toLowerCase().includes(term) ||
            String(task.id).includes(term) ||
            String(task.parent_id).includes(term) ||
            String(task.grandparent_id).includes(term);

        if (taskMatches) return true;

        // Check related PRs
        if (task.relatedPRs?.length) {
            return task.relatedPRs.some(item =>
                String(item.pr.pullRequestId).includes(term) ||
                item.pr.title?.toLowerCase().includes(term) ||
                item.project?.toLowerCase().includes(term)
            );
        }

        return false;
    }

    prMatchesSearch(pr: any, projectName: string): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return true;
        }
        return pr.title?.toLowerCase().includes(term) ||
            String(pr.pullRequestId).includes(term) ||
            projectName?.toLowerCase().includes(term);
    }

    userHasResults(userId: number): boolean {
        const term = this.taskSearchTerm().trim().toLowerCase();
        if (!term) {
            return true;
        }
        const hasMatchingTask = this.userTasks()[userId]?.some(task => this.taskMatchesSearch(task));
        const hasMatchingPR = this.userPRs()[userId]?.some(projectPR =>
            projectPR.prs?.some(pr => this.prMatchesSearch(pr, projectPR.project))
        );
        return hasMatchingTask || hasMatchingPR;
    }


    saveChangedDisplay(changedState: boolean) {
        this.showChanged.set(changedState);
        localStorage.setItem('daily_show_changed', changedState.toString());
    }

    updateLastClickedTask(task: AzureTask) {
        this.lastSelectedTask.set(task.id);
        localStorage.setItem('last_selected_task', String(task.id));
    }

    isHighPriorityBug(task: AzureTask): boolean {
        const isBug = task.workItemType?.toLowerCase() === 'bug';
        const isHighSeverity = task.severity === '2 - High' || task.severity === '1 - Critical';
        return isBug && isHighSeverity;
    }

    isCriticalBug(task: AzureTask): boolean {
        return task.workItemType?.toLowerCase() === 'bug' && task.severity === '1 - Critical';
    }

    isDraggableTask(task: AzureTask): boolean {
        return !this.isHighPriorityBug(task);
    }

    dropTask(event: CdkDragDrop<AzureTask[]>, userId: number) {
        const tasks = [...this.userTasks()[userId]];
        moveItemInArray(tasks, event.previousIndex, event.currentIndex);
        this.userTasks.update(current => ({...current, [userId]: tasks}));

        this.subscriptions.add(
            this.azureService.sortTasks(tasks.map(task => task.id)).subscribe({
                next: () => console.log('Task order saved successfully'),
                error: err => console.error('Error saving task order:', err)
            })
        );
    }

    private sortTasksForUser(userId: number, tasks: AzureTask[]): AzureTask[] {
        if (userId === 15) {
            const priority = (task: AzureTask): number => {
                if (task.workItemType === 'Bug' && task.severity === '1 - Critical') return 0;
                if (task.workItemType === 'Bug' && task.severity === '2 - High') return 1;
                if (task.state === 'Active') return 2;
                if (task.state === 'Te testen') return 3;
                if (task.state === 'New') return 4;
                if (task.state === 'On hold') return 5;
                return 6;
            };

            return [...tasks].sort((a, b) =>
                priority(a) - priority(b)
                || a.project.localeCompare(b.project)
                || new Date(b.changed_date).getTime() - new Date(a.changed_date).getTime()
            );
        }
        return tasks;
    }

    absentInfo(userId: number, week: string, isoDay: number): { absent: boolean; color: string | null; startMinutes: number; endMinutes: number; leaveTypeName?: string; begintime?: string; endtime?: string } {
        return this.absentLookup()[userId]?.[week]?.[isoDay] ?? {absent: false, color: null, startMinutes: 0, endMinutes: 0};
    }

    formatAbsentTooltip(absent: any): string {
        if (!absent.absent) return '';
        const parts: string[] = [];
        if (absent.leaveTypeName) parts.push(absent.leaveTypeName);
        if (absent.begintime && absent.endtime) {
            parts.push(`${absent.begintime.slice(0, 5)} - ${absent.endtime.slice(0, 5)}`);
        }
        return parts.join('\n');
    }

    weekStringToMonday(weekStr: string): Date {
        const [year, week] = weekStr.split('-').map(Number);
        const jan4 = new Date(Date.UTC(year, 0, 4)); // Jan 4 is always in week 1
        const dayOfWeek = jan4.getUTCDay() || 7;
        const monday = new Date(jan4);
        monday.setUTCDate(jan4.getUTCDate() - (dayOfWeek - 1) + (week - 1) * 7);
        return monday;
    }

    @HostListener('window:keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        if (event.ctrlKey === true && event.key === "f") {
            event.preventDefault();
            this.taskSearch.nativeElement.focus();
        }

        if (event.key == 'Escape') {
            this.taskSearchTerm.set('');
            this.taskSearch.nativeElement.value = '';
        }
    }

    protected readonly Date = Date;
}
